import React, { useState, useEffect } from 'react';
import { Gift, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable "Gift this" flow. Collects recipient/sender/message, then starts a
 * gift checkout. After payment the buyer gets a shareable gift link on the
 * success page to forward to their friend.
 *
 * Self-contained modal (no shadcn/Radix Dialog) so the dev visual-edit tooling
 * doesn't have to trace props across the Radix boundary.
 */
export default function GiftModal({
  productId,
  productLabel = 'this',
  buttonLabel = 'Gift it',
  buttonClassName = 'village-btn village-btn--secondary',
  testId = 'gift-open-btn',
}) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sender, setSender] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  async function startGift(e) {
    e.preventDefault();
    if (!recipient.trim() || !sender.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          origin_url: window.location.origin,
          is_gift: true,
          gift_recipient: recipient.trim(),
          gift_recipient_email: recipientEmail.trim(),
          gift_sender: sender.trim(),
          gift_message: message.trim(),
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setLoading(false);
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  const inputCls =
    'w-full rounded-lg border border-[#d4af37]/35 bg-black/30 px-3 py-2.5 text-[#f5ecd7] text-sm placeholder:text-[#f5ecd7]/40 focus:outline-none focus:border-[#d4af37]';

  return (
    <>
      <button type="button" className={buttonClassName} data-testid={testId} onClick={() => setOpen(true)}>
        <Gift size={16} strokeWidth={2.2} /> {buttonLabel}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80"
          data-testid="gift-modal-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div
            className="relative w-full max-w-lg rounded-xl border border-[#d4af37]/40 bg-[#120c24] text-[#f5ecd7] p-6 shadow-2xl"
            data-testid="gift-modal"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 text-[#f5ecd7]/60 hover:text-[#f5ecd7] transition-opacity"
              data-testid="gift-close-btn"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="mb-4 pr-6">
              <h2 className="text-[#f8e7b0] text-xl" style={{ fontFamily: 'Georgia, serif' }}>
                🎁 Gift {productLabel}
              </h2>
              <p className="text-[#f5ecd7]/70 text-sm mt-1">
                Add their email and we&apos;ll send the gift straight to their inbox
                after you pay — you&apos;ll also get a shareable link as backup.
              </p>
            </div>

            <form onSubmit={startGift} className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#d4af37] mb-1">
                  Who&apos;s it for?
                </label>
                <input
                  className={inputCls}
                  placeholder="Their first name"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  required
                  data-testid="gift-recipient-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#d4af37] mb-1">
                  Their email <span className="text-[#f5ecd7]/40 normal-case tracking-normal">(optional — we&apos;ll email it to them)</span>
                </label>
                <input
                  type="email"
                  className={inputCls}
                  placeholder="their@email.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  data-testid="gift-recipient-email-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#d4af37] mb-1">
                  From
                </label>
                <input
                  className={inputCls}
                  placeholder="Your name"
                  value={sender}
                  onChange={(e) => setSender(e.target.value)}
                  required
                  data-testid="gift-sender-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#d4af37] mb-1">
                  A note (optional)
                </label>
                <textarea
                  className={inputCls}
                  rows={3}
                  placeholder="Thinking of you, sister. 🌙"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  data-testid="gift-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="village-btn village-btn--primary w-full justify-center"
                data-testid="gift-checkout-btn"
              >
                {loading ? (
                  <><Loader2 size={16} className="animate-spin" /> Opening checkout…</>
                ) : (
                  <><Gift size={16} strokeWidth={2.2} /> Continue to Gift Checkout</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
